import React from 'react'

function dateKey(d) {
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
}
function timeToMinutes(t) {
  if (!t) return Infinity
  const [hm, ap] = t.split(' ')
  let [h, m] = hm.split(':').map(Number)
  if (ap === 'PM' && h !== 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return h * 60 + (m || 0)
}

export default function PlanMultiDayPrint({ dates, jobs, clients, staff, routeStaffMap, onClose }) {
  const scheduledJobs = (jobs||[]).filter(j => j.status === 'scheduled')

  function clientInfo(id) { return clients.find(c=>c.id===id) }
  function staffNamesFor(routeVal, dk) {
    const ids = (routeStaffMap && routeStaffMap[dk + '|' + routeVal]) || []
    return ids.map(id => staff.find(s=>s.id===id)?.name).filter(Boolean).map(n => n.trim().split(/\s+/)[0])
  }

  // Visual of who is where, hour by hour: one row per person, one block per house.
  // Same house = same color, so people working together line up in the same color.
  function CrewTimeline({ dk, dayJobs, routes }) {
    const timed = dayJobs.filter(j => j.route && timeToMinutes(j.start_time) !== Infinity)
    if (timed.length === 0) return null

    // person -> their jobs for the day (from every route they're on)
    const byPerson = {}
    const routesOf = {}
    routes.forEach(r => {
      const ids = (routeStaffMap && routeStaffMap[dk + '|' + r]) || []
      const rJobs = timed.filter(j => j.route === r)
      ids.forEach(id => {
        if (!byPerson[id]) byPerson[id] = []
        if (!routesOf[id]) routesOf[id] = []
        if (rJobs.length) routesOf[id].push(r)
        rJobs.forEach(j => byPerson[id].push(j))
      })
    })
    const people = Object.keys(byPerson)
      .map(id => ({ id, name: (staff.find(s => s.id === id)?.name || '?').trim().split(/\s+/)[0], jobs: byPerson[id], team: (routesOf[id] || []).join(' + ') }))
      .filter(p => p.jobs.length > 0)
      .sort((a, b) => Math.min(...a.jobs.map(j => timeToMinutes(j.start_time))) - Math.min(...b.jobs.map(j => timeToMinutes(j.start_time))) || a.name.localeCompare(b.name))
    if (people.length === 0) return null

    // Team = the set of routes a person is on. People on the same team are
    // grouped together and boxed off in that team's hue.
    const TEAM_HUES = [
      { bar: '#22c55e', tint: '#f0fdf4' }, // Ruta 1
      { bar: '#3b82f6', tint: '#eff6ff' }, // Ruta 2
      { bar: '#f97316', tint: '#fff7ed' }, // Ruta 3
      { bar: '#eab308', tint: '#fefce8' }, // Ruta 4
      { bar: '#a855f7', tint: '#faf5ff' },
      { bar: '#ec4899', tint: '#fdf2f8' },
    ]
    const teamMap = {}
    people.forEach(p => { (teamMap[p.team] = teamMap[p.team] || []).push(p) })
    const firstStart = list => Math.min(...list.flatMap(p => p.jobs.map(j => timeToMinutes(j.start_time))))
    const teams = Object.entries(teamMap)
      .map(([team, members]) => ({ team, members: members.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => (parseInt(a.team) || 99) - (parseInt(b.team) || 99) || a.team.length - b.team.length || firstStart(a.members) - firstStart(b.members))
    const hueOf = team => TEAM_HUES[((parseInt(team) || 1) - 1 + (team.includes('+') ? 4 : 0)) % TEAM_HUES.length]

    const startOf = j => timeToMinutes(j.start_time)
    const endOf = j => startOf(j) + (parseFloat(j.hours) || 2) * 60
    const t0 = Math.floor(Math.min(...timed.map(startOf)) / 60) * 60
    const t1 = Math.ceil(Math.max(...timed.map(endOf)) / 60) * 60
    const span = Math.max(t1 - t0, 60)
    const pct = m => ((m - t0) / span) * 100

    // one color per house, in order of start time
    const PALETTE = ['#fde2e8', '#dbeafe', '#dcfce7', '#fef3c7', '#ede9fe', '#ffedd5', '#cffafe', '#f3e8ff', '#e0e7ff', '#fce7f3']
    const BORDERS = ['#e8547a', '#3b82f6', '#22c55e', '#d97706', '#8b5cf6', '#ea580c', '#0891b2', '#a855f7', '#4f46e5', '#db2777']
    const houseOrder = [...timed].sort((a, b) => startOf(a) - startOf(b)).map(j => j.id)
    const colorOf = j => houseOrder.indexOf(j.id) % PALETTE.length

    const hours = []
    for (let m = t0; m <= t1; m += 60) hours.push(m)
    const hrLabel = m => { const h = Math.floor(m / 60); return (h % 12 || 12) + (h < 12 ? 'a' : 'p') }

    const NAME_W = 50
    return (
      <div style={{ marginTop: 6, marginBottom: 4, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#202124', marginBottom: 3 }}>👥 Equipo por hora</div>
        {/* hour ruler */}
        <div style={{ position: 'relative', height: 12, marginLeft: NAME_W + 8, marginRight: 4 }}>
          {hours.map(m => (
            <span key={m} style={{ position: 'absolute', left: pct(m) + '%', transform: 'translateX(-50%)', fontSize: 8, color: '#80868b' }}>{hrLabel(m)}</span>
          ))}
        </div>
        {teams.map(({ team, members }) => {
          const hue = hueOf(team)
          return (
            <div key={team} style={{ background: hue.tint, borderLeft: '4px solid ' + hue.bar, border: '1px solid ' + hue.bar, borderLeftWidth: 4, borderRadius: 4, padding: '2px 3px 1px', marginBottom: 5 }}>
              <div style={{ fontSize: 8, fontWeight: 800, color: hue.bar, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 1 }}>Ruta {team}</div>
              {members.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', height: 17, marginBottom: 2 }}>
                <div style={{ width: NAME_W, flexShrink: 0, fontSize: 9, fontWeight: 700, color: '#202124', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', paddingRight: 4 }}>{p.name}</div>
                <div style={{ position: 'relative', flex: 1, height: '100%', background: '#fafafa', borderRadius: 3 }}>
                  {hours.map(m => (
                    <div key={m} style={{ position: 'absolute', left: pct(m) + '%', top: 0, bottom: 0, borderLeft: '1px dotted #ddd' }} />
                  ))}
                  {p.jobs.map(j => {
                    const cl = clientInfo(j.client_id)
                    const c = colorOf(j)
                    return (
                      <div key={j.id} title={(cl?.nickname || cl?.name || '?')}
                        style={{ position: 'absolute', left: pct(startOf(j)) + '%', width: (pct(endOf(j)) - pct(startOf(j))) + '%', top: 1, bottom: 1,
                          background: PALETTE[c], border: '1px solid ' + BORDERS[c], borderRadius: 3, fontSize: 8, lineHeight: '13px',
                          padding: '0 3px', overflow: 'hidden', whiteSpace: 'nowrap', color: '#202124', boxSizing: 'border-box' }}>
                        {cl?.nickname || cl?.name || '?'}
                      </div>
                    )
                  })}
                </div>
              </div>
              ))}
            </div>
          )
        })}
      </div>
    )
  }

  // Pair up days, two per "page" — mirrors how she splits a physical page in half
  const pairs = []
  for (let i = 0; i < dates.length; i += 2) pairs.push(dates.slice(i, i+2))

  function DayColumn({ date }) {
    const dk = dateKey(date)
    const dayJobs = scheduledJobs.filter(j => j.scheduled_date === dk)
    const routes = Array.from(new Set(dayJobs.filter(j=>j.route).map(j=>j.route))).sort()
    const dayLabel = date.toLocaleDateString('es-MX', { weekday:'long', day:'numeric', month:'long' })

    return (
      <div style={{flex:'1 1 320px',minWidth:280,padding:'0 6px'}}>
        <div style={{fontSize:15,fontWeight:800,color:'#202124',textTransform:'capitalize',borderBottom:'2px solid #202124',paddingBottom:5,marginBottom:10}}>
          {dayLabel}
        </div>
        {routes.length===0 ? (
          <div style={{color:'#9aa0a6',fontSize:12,padding:'8px 0'}}>Sin rutas</div>
        ) : routes.map(r => {
          const rJobs = dayJobs.filter(j=>j.route===r).sort((a,b)=>timeToMinutes(a.start_time)-timeToMinutes(b.start_time))
          return (
            <div key={r} style={{marginBottom:12}}>
              <div style={{fontSize:11,marginBottom:3}}>
                <span style={{fontWeight:800,color:'#e8547a'}}>Ruta {r}</span>
                {staffNamesFor(r, dk).length>0 && (
                  <span style={{fontWeight:700,color:'#185fa5',marginLeft:6}}>· {staffNamesFor(r, dk).join(' + ')}</span>
                )}
              </div>
              {rJobs.map(job => {
                const cl = clientInfo(job.client_id)
                return (
                  <div key={job.id} style={{fontSize:13,color:'#202124',padding:'2px 0',display:'flex',gap:8}}>
                    <span style={{fontWeight:700,minWidth:66,flexShrink:0,color:'#80868b'}}>{job.start_time||'—'}</span>
                    <span>{cl?.nickname||cl?.name||'?'}</span>
                  </div>
                )
              })}
            </div>
          )
        })}
        {routes.length>0 && <CrewTimeline dk={dk} dayJobs={dayJobs} routes={routes} />}
      </div>
    )
  }

  return (
    <div style={ov.overlay} className="pmd-overlay">
      <style>{`
        @media print {
          @page { size: portrait; margin: 12mm; }
          body * { visibility: hidden; }
          .pmd-printarea, .pmd-printarea * { visibility: visible; }
          .pmd-printarea { position: absolute; left: 0; top: 0; width: 100%; }
          .pmd-noprint { display: none !important; }
          .pmd-pair { display: flex !important; flex-wrap: nowrap !important; page-break-after: always; }
          .pmd-pair:last-child { page-break-after: auto; }
          .pmd-pair > div:first-child { border-right: 1px solid #ddd; }
        }
      `}</style>
      <div style={ov.modal}>
        <div style={ov.header} className="pmd-noprint">
          <div style={{fontSize:16,fontWeight:700}}>🖨️ Plan de varios días — vista rápida</div>
          <div style={{display:'flex',gap:8}}>
            <button style={ov.printBtn} onClick={()=>window.print()}>🖨️ Imprimir</button>
            <button style={ov.closeBtn} onClick={onClose}>×</button>
          </div>
        </div>

        <div style={ov.body} className="pmd-printarea">
          {pairs.map((pair, pi) => (
            <div key={pi} className="pmd-pair" style={{display:'flex',flexWrap:'wrap',gap:16,marginBottom:28,paddingBottom:pi<pairs.length-1?20:0,borderBottom:pi<pairs.length-1?'1px solid #eee':'none'}}>
              {pair.map((date,di) => <DayColumn key={di} date={date} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const ov = {
  overlay: {position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:400,display:'flex',alignItems:'center',justifyContent:'center'},
  modal: {background:'#fff',borderRadius:14,width:'96vw',maxWidth:1300,maxHeight:'92vh',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'},
  header: {display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  printBtn: {padding:'8px 16px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer'},
  closeBtn: {fontSize:22,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6'},
  body: {overflowY:'auto',padding:'24px 28px'},
}
